/**
 * Two real, checkable setup steps for the dashboard's setup card.
 *
 * Deliberately not three. "Connect your ATS" was cut from this list: there is
 * no account-level ATS-connection setting anywhere in the API today (the
 * per-job "Not connected" ATS-ID field is TheirStack ingestion data, not
 * something a user can act on), and a checklist item with nowhere to send
 * the user is worse than the empty-state apology it would replace.
 *
 * Calendar completion reads directly off the already-loaded `user` --
 * User.calendarLinkUrl is null until set. Team size needs its own request:
 * `pageSize=1` because only `totalElements` is read, matching the pattern
 * useDashboardData.ts already uses for /api/jobs.
 */

import { useEffect, useState } from 'react';
import axios from '../../../axiosConfig';
import { User } from '../../../fsm/users';

export interface SetupChecklistState {
  calendarConnected: boolean;
  /** null while the team-size request is still in flight. */
  teammateInvited: boolean | null;
}

export const useSetupChecklist = (user?: User): SetupChecklistState => {
  const [teammateInvited, setTeammateInvited] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/users?pageNumber=1&pageSize=1')
      .then((res) => {
        if (cancelled) return;
        const totalElements = res.data?.totalElements ?? 0;
        setTeammateInvited(totalElements > 1);
      })
      .catch(() => {
        // Left null: an unknown state is not the same as "not done yet",
        // and the card's own render logic treats null as "don't claim this
        // is pending" -- it just doesn't show a checkmark either.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    calendarConnected: Boolean(user?.calendarLinkUrl),
    teammateInvited,
  };
};
